import { useFormik } from 'formik';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useEffect, useCallback } from 'react';
import { Grid } from '@mui/material';
import { IconContext } from 'react-icons';

import Core from '../../Core';
import colors from '../../../styles/colors';
import CardSomos from '../../../components/Card';
import useWindowSize from '../../../hooks/useWindowSize';
import * as api from '../../../services/api';
import { DefaultMessage } from '../../../utils/defaultMessages';

import { booleanChips, fields, productsData } from '../../../components/CreateOrUpdateCompany/FormFields';
import { handleAddProduct } from '../../../components/CreateOrUpdateCompany/Helpers';
import { ProductItem } from '../../../components/CreateOrUpdateCompany/ProductItem/index';
import  BooleanChips  from '../../../components/CreateOrUpdateCompany/BooleanChips';
import { TextFieldsGrid } from '../../../components/CreateOrUpdateCompany/TextFieldsGrid';
import { PlanSelectorChip } from '../../../components/CreateOrUpdateCompany/PlanSelectorChip';
import { companyValidationSchema } from '../../../components/CreateOrUpdateCompany/validationSchema';
import Info from '../../../components/CreateOrUpdateCompany/Info';
import { ButtonInverse } from '../../../components/ButtonInverse';

const colheitadeira = require('../../../images/whiteHarvest.png');

export default function CreateOrUpdateCompany(): JSX.Element {
  const { id } = useParams();
  const { width } = useWindowSize();

  const {
    handleChange,
    handleSubmit,
    values,
    setFieldValue,
    errors,
    resetForm,
    touched,
  } = useFormik({
    initialValues: {
      cnpj: '', cep: '', email: '', number: '', api_user: false,
      products: [], fantasy_name: '', social_reason: '',
      can_query_chassi_or_plate: false,
      can_query_chassi_or_plate_bus_truck: false,
      can_download_full_table: false, is_admin: false, plan: '1',
      contract_end: '', contract_start: '',
      tr_balance: 0, hp_balance: 0, sr_balance: 0,
      cr_balance: 0, cm_balance: 0, hv_balance: 0, lw_balance: 0,
    },
    validationSchema: companyValidationSchema,
    onSubmit: createOrUpdateCompany,
  });

  async function createOrUpdateCompany() {
    toast.dismiss();
    toast.loading('Aguarde.', { position: toast.POSITION.TOP_RIGHT });
    const endpoint = id ? `company/update/${id}/` : 'company/create/';

    const payload: Record<string, any> = { ...values };
    ['tr_balance', 'hp_balance', 'sr_balance', 'cr_balance', 'cm_balance', 'hv_balance', 'lw_balance']
      .forEach(k => { if (!payload[k]?.toString().length) payload[k] = 0 });

    const request = id ? api.put : api.post;
    try {
      const response: any = await request(endpoint, payload);
      toast.dismiss();
      if ([200, 201].includes(response.status)) {
        toast.success(`Empresa ${id ? 'atualizada' : 'cadastrada'} com sucesso!`, {
          position: toast.POSITION.TOP_RIGHT,
        });
        if (!id) {
          resetForm();
        }
      } else {
        toast.error(response.data?.content || DefaultMessage.ERROR, {
          position: toast.POSITION.TOP_RIGHT,
        });
      }
    } catch {
      toast.dismiss();
      toast.error(DefaultMessage.ERROR, { position: toast.POSITION.TOP_RIGHT });
    }
  }

  const getData = useCallback(async () => {
    try {
      const res: any = await api.get(`company/read/${id}/`);
      const content = res.data.content;
      Object.entries(content).forEach(([key, val]) => setFieldValue(key, val));
    } catch {
      toast.error(DefaultMessage.ERROR, { position: toast.POSITION.TOP_RIGHT });
    }
  }, [id, setFieldValue]);

  useEffect(() => { if (id) getData(); }, [getData, id]);

  return (
    <IconContext.Provider value={{ size: '18' }}>
      <Core withNavbar withBlueBar path="/companies" label="Clique aqui para acessar a listagem de empresas">
        <Grid container direction="column" alignItems="center" textAlign="center" pb={10}>
          <form onSubmit={handleSubmit}>
            <TextFieldsGrid
              sx={width > 1200 ? { width: 330 } : undefined}
              sxMask={width > 1200 ? { width: 330 } : undefined}
              fields={fields}
              values={values}
              setFieldValue={setFieldValue}
              handleChange={handleChange}
              errors={errors}
              touched={touched}
            />
            <PlanSelectorChip value={values.plan} setValue={(val: any) => setFieldValue('plan', val)} />
            <Grid style={{padding: '1rem'}}>
              <BooleanChips
                styleContainer={{
                  display: width < 900 ? 'block' : 'flex',
                  alignItems: width < 900 ? 'center' : 'end',
                }}
                paddingX={`${width > 600 ? '10' : '2'}`}
                items={booleanChips}
                values={values}
                setFieldValue={setFieldValue}
                mb='4'
              />

            </Grid>

            <CardSomos>
              <Grid container textAlign="center" justifyContent="center" spacing={4}>
                <Info values={values} />
                {productsData.map(product => (
                  <ProductItem
                    key={product.id}
                    product={product}
                    values={values}
                    addProduct={(id) => handleAddProduct(id, values, setFieldValue)}
                    handleChange={handleChange}
                    colors={colors}
                    colheitadeira={colheitadeira}
                  />
                ))}
              </Grid>
            </CardSomos>

            <Grid container spacing={2} justifyContent="center" px={width > 600 ? 20 : 2}>
              <Grid item>
                <ButtonInverse variant="contained" size="large" color="secondary" type="submit">
                  {id ? 'Atualizar' : 'Cadastrar'}
                </ButtonInverse>
              </Grid>
            </Grid>
          </form>
        </Grid>
      </Core>
    </IconContext.Provider>
  );
}